import { telefoneVigente } from "../../../shared/utils/telefoneBR";
import {
  textoSombraVerificador,
  linhasAchadosSombra,
  registrosSombra,
} from "../services/sombraVerificadorTexto";
import {
  segmentosCitados,
  listarSegmentos,
} from "../../../shared/utils/segmentosHepaticos";
import {
  REGRA_TUMORES_MULTIPLOS,
  REGRA_LIRADS_ACHADO_MAIOR,
  REGRA_BIOPSIA_CRITERIO,
} from "../../ia-analise/services/regrasClinicas";
import { textosDosProblemas } from "../../../shared/utils/textoDoProblema";
import React, { useState, useEffect } from "react";
import {
  FileText,
  FileDown,
  Settings,
  ChevronDown,
  ChevronUp,
  ShieldCheck,
} from "lucide-react";
import { Paciente } from "../../../types";
import { theme } from "../../../shared/styles/theme";
import { useAuth } from "../../auth/context/AuthContext";
import { useConfigDocumentoSalva } from "../hooks/useConfigDocumentoSalva";
import { ehMedico } from "../../../shared/utils/permissoesCategoria";
import { Bot, Loader2 } from "lucide-react";
import { coletarDadosCompletos } from "../../ia-analise/services/coletarDadosPaciente";
import {
  meldMaisRecenteDeExames,
  formatarDataBRMeld,
  type MeldDeExame,
} from "../../../shared/utils/meldDeExame";
import {
  gerarPDFTextoLivre,
  montarTextoLivreHTML,
} from "../services/gerarPDF";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../../../lib/firebase";
import { callAnalyze } from "../../../lib/analyzeClient";
import ModalAssinatura from "./ModalAssinatura";
import { DadosAssinaturaWidget } from "../services/assinaturaCliente";
import {
  montarSeloAssinaturaDigital,
  dataHoraSeloAgora,
} from "../services/seloAssinatura";
import {
  gerarCodigoValidacao,
  montarBlocoQR,
} from "../../validacoes/services/validacaoCliente";
import {
  REGRAS_LAUDO_PREVIDENCIARIO,
  REGRA_NUTRICAO,
  REGRA_MELD_30,
  REGRA_TERMINOLOGIA,
  REGRA_FIDELIDADE,
  REGRA_ETIOLOGIA_NAO_INFERIDA,
} from "../../ia-analise/services/regrasClinicas";
// B289/B212 (AI_131): GO-LIVE DE PRODUÇÃO CONCLUÍDO (29/06/2026) → assinatura
// habilitada para todos os médicos (o gate `ehMedico` abaixo barra não-médicos).
// Antes ficava atrás da flag local `assinar_habilitado` (só nos navegadores de teste).
const ASSINATURA_HABILITADA = true;

interface TextoLivreProps {
  paciente: Paciente;
  dadosIniciais?: any;
  onPrescricaoSalva?: () => void;
}

export default function TextoLivre({
  paciente,
  dadosIniciais,
  onPrescricaoSalva,
}: TextoLivreProps) {
  const { usuarioLogado } = useAuth();
  const [titulo, setTitulo] = useState("");
  const [conteudo, setConteudo] = useState("");
  const [cidade, setCidade] = useState("Porto Velho - RO");
  const [telefone, setTelefone] = useState("(69) 3216-5700");

  // B289 (AI_127): dados do modal de assinatura digital (null = fechado).
  const [modalAssinatura, setModalAssinatura] = useState<{
    html: string;
    tipo: string;
    prescricaoId: string;
    dados: DadosAssinaturaWidget;
    nomeArquivo: string;
    codigoValidacao?: string;
    dadosValidacao?: {
      medicoNome: string;
      registro: string;
      pacienteTelefone: string;
      pacienteNascimento: string;
    };
  } | null>(null);

  // Carregar dados do histórico
  useEffect(() => {
    if (dadosIniciais?.conteudo) {
      const texto =
        typeof dadosIniciais.conteudo === "string"
          ? dadosIniciais.conteudo
          : dadosIniciais.conteudo.texto || "";
      setConteudo(texto);
      if (dadosIniciais.titulo) setTitulo(dadosIniciais.titulo);
      if (dadosIniciais.config?.nomeAmbulatorio)
        setNomeAmbulatorio(dadosIniciais.config.nomeAmbulatorio);
      if (dadosIniciais.config?.endereco)
        setEndereco(dadosIniciais.config.endereco);
    }
  }, [dadosIniciais]);

  const [nomeAmbulatorio, setNomeAmbulatorio] = useState(
    "Ambulatório de Transplante Hepático de Rondônia"
  );
  const [endereco, setEndereco] = useState("Av. Gov. Jorge Teixeira, 3766");
  // AI_154 — configuração do documento salva por usuário (usuarios/{uid}).
  const { marcarEdicao: marcarEdicaoConfig } = useConfigDocumentoSalva(
    usuarioLogado?.id,
    { nomeAmbulatorio, endereco, cidade, telefone },
    { setNomeAmbulatorio, setEndereco, setCidade, setTelefone },
    !!dadosIniciais?.config
  );
  const [mostrarConfig, setMostrarConfig] = useState(false);
  const [loadingIA, setLoadingIA] = useState(false);
  const [tipoLaudoIA, setTipoLaudoIA] = useState("");
  const [verificacaoIA, setVerificacaoIA] = useState<any>(null);
  const [conteudoOriginalIA, setConteudoOriginalIA] = useState<string | null>(
    null
  );
  const [laudoIdIA, setLaudoIdIA] = useState<string | null>(null);
  const [provenienciaIA, setProvenienciaIA] = useState<Record<string, unknown> | null>(null);

  // B368 (AI_156, decisão de Leonardo): UM laudo só de Situação Especial.
  // O que variava entre a Central Estadual (SP) e a Nacional era só SE a
  // hipertensão portal conta como critério — e decidir isso no laudo era nós
  // antecipando a doutrina de quem revisa. A regra passa a ser documental:
  // lista-se o que os dados sustentam (irressecabilidade + HP juntos, quando
  // ambos existirem), e a câmara aplica a régua dela. Não havendo nenhum, a
  // seção sai VAZIA — a revisão humana escreve, que é melhor que a IA inventar
  // critério ou se desculpar dentro do documento.
  const instrucaoSituacaoEspecial = () => {
  const BLOCO_HP =
    `- Hipertensão portal clinicamente significativa: varizes esofágicas à endoscopia OU sinais de imagem (esplenomegalia com medida, circulação colateral / recanalização da veia paraumbilical, calibre do tronco portal aumentado) + plaquetopenia (citar valores e datas). Se estiver documentada, LISTE-A com os valores — mesmo que outro critério também esteja atendido.
🩸 HEMORRAGIA DIGESTIVA VARICOSA PRÉVIA (B368): sangramento por varizes já ocorrido é a evidência CLÍNICA MAIS FORTE de hipertensão portal clinicamente significativa — mais forte que calibre de variz ou contagem de plaquetas. Se os dados registrarem HDA varicosa prévia, CITE-A nesta seção junto da hipertensão portal, com o número de episódios e a data do mais recente quando houver. NÃO a deixe apenas no diagnóstico de base.`;

    return `Gere um relatório formal para solicitação de Situação Especial por Carcinoma Hepatocelular junto à Central de Transplantes. NÃO cite NENHUMA lei, portaria ou nota técnica no título nem em nenhuma seção do laudo — nem a federal (Portaria GM/MS 8.041/2025) nem qualquer norma estadual. Apresente os critérios de forma clínica, sem nomear base legal. (Motivo: os critérios de irressecabilidade divergem entre as câmaras técnicas estadual e nacional; nomear uma norma específica no laudo fragiliza o pedido.)

      ${REGRA_ETIOLOGIA_NAO_INFERIDA}

      Siga EXATAMENTE esta estrutura:

**SOLICITAÇÃO DE SITUAÇÃO ESPECIAL POR CARCINOMA HEPATOCELULAR**

**DIAGNÓSTICO DE BASE**
Diagnóstico da cirrose com etiologia, classificação Child-Pugh com pontuação, MELD atual. Para o RGCT, busque o campo "numeroRGCT" dentro do objeto "inscricaoTx" nos dados JSON do paciente e cite como "RGCT nº [valor]". O campo aparece como: inscricaoTx.numeroRGCT. Se encontrar o valor, use-o diretamente. Exemplo: se numeroRGCT = "349159-5", escreva "RGCT nº 349159-5". Mencione ausência de descompensações se aplicável.
⛔ NÃO cite ELASTOGRAFIA hepática neste laudo (nem o valor em kPa, nem FibroScan/ARFI/2D-SWE, nem METAVIR derivado dela) e NÃO apoie a cirrose nela. Motivo: o valor pontual abre discussão sobre ponto de corte (10 kPa é "sugestivo", não confirmatório na maioria dos métodos) e não acrescenta nada a um caso em que a cirrose já está estabelecida pelo conjunto clínico, laboratorial e de imagem. Afirme a cirrose com o diagnóstico e a etiologia, sem enumerar as provas que a sustentam.

**LESÕES HEPÁTICAS — EXAME DE IMAGEM**
Para CADA lesão identificada, descreva: método de imagem, data do exame (OBRIGATÓRIO citar a data), localização (segmento), dimensões em cm, características radiológicas (realce arterial, washout, pseudocápsula, restrição à difusão), classificação LI-RADS. Distingua claramente lesões CONFIRMADAS (LI-RADS 5) de SUSPEITAS.
⛔ SEJA INCISIVO NO DIAGNÓSTICO: ao classificar a lesão como LI-RADS 5, AFIRME o carcinoma hepatocelular de forma direta e pare aí. NÃO explique o método diagnóstico nem justifique a ausência de biópsia (⚠️ mas se HOUVER anatomopatológico confirmando CHC, CITE-O — ver a regra de biópsia abaixo: a proibição é contra justificar a AUSÊNCIA de histologia, nunca contra apresentar uma que existe) — proibidas frases como "diagnóstico estabelecido por critérios de imagem", "dispensando biópsia", "sem necessidade de confirmação histológica", "critério não invasivo", "conforme critérios radiológicos consagrados" e equivalentes. Quem lê o laudo é a Câmara Técnica de transplante: a equivalência LI-RADS 5 = CHC é premissa conhecida, e explicá-la soa defensivo e enfraquece o documento.

**CRITÉRIOS DE MILÃO**
Avalie explicitamente se o paciente está dentro dos Critérios de Milão (lesão única ≤5cm ou até 3 lesões ≤3cm). Se houver lesão suspeita não confirmada, mencione que a avaliação considera apenas lesões confirmadas.

**MARCADORES TUMORAIS**
AFP com valor numérico e data da coleta (OBRIGATÓRIO). Série histórica se disponível.

**CRITÉRIOS DE IRRESSECABILIDADE HEPÁTICA**
Esta seção é OBRIGATÓRIA no laudo. A Central de Transplantes espera pelo menos um critério — então liste TODOS os que os dados sustentarem. Mas isso é uma expectativa da Central, NÃO uma licença para produzir um critério: se os dados não sustentarem nenhum, a seção fica VAZIA (ver abaixo). NUNCA escreva marcador de preenchimento — "[COMPLETAR]", "[VERIFICAR]", "[A PREENCHER]" e equivalentes são BLOQUEADOS pelo sistema e reprovam o laudo; se não há o que escrever, não escreva nada. NO LAUDO, ao abrir esta seção, use EXATAMENTE a frase "Conforme os critérios de irressecabilidade:" — NÃO cite nenhuma lei, portaria, nota técnica nem número de formulário como base (nem estadual, nem a federal 8.041/2025); apresente os critérios de forma puramente clínica, SEM ancoragem legislativa no texto do laudo. Liste TODOS os critérios presentes, com valores e datas dos exames:
- MELD > 10: citar valor e data do exame
- Child-Pugh B ou C: citar classe, pontuação e data
- Sódio sérico < 135 mEq/L: citar valor e data
- Necessidade de ressecção de mais de 2 segmentos em um único procedimento cirúrgico
- Tumores múltiplos em segmentos distintos: descrever
${BLOCO_HP}
🔬 ANCORAGEM DOS CRITÉRIOS ANATÔMICOS (B367 — OBRIGATÓRIO): "ressecção de mais de 2 segmentos" e "tumores múltiplos em segmentos distintos" NÃO são critérios objetivos como MELD/Child/sódio — são LEITURA do exame. A análise do exame de imagem JÁ FOI FEITA e está nos dados (campos de achados de imagem: lesões hepáticas, conclusão, resumo e o literal do laudista, além do bloco "LESÕES HEPÁTICAS" dos FATOS VERIFICADOS): LEIA-A antes de listar ou omitir um critério anatômico — não a trate como ausente. Só liste um deles se o LAUDO DO EXAME o sustentar, e CITE o achado como está escrito, SEM podar palavra que o enfraqueça (ex.: se o laudo diz "nódulo no segmento V ADJACENTE ao supracitado", a palavra "adjacente" DEVE aparecer; omiti-la para sustentar "segmentos distintos" é falsear o exame e o revisor confere contra o anexo). Se as lesões ocupam o MESMO segmento, ou segmentos contíguos cuja ressecção caberia em dois segmentos, NÃO afirme "segmentos distintos" nem "mais de 2 segmentos".
🔬 ${REGRA_TUMORES_MULTIPLOS}
🩻 ${REGRA_LIRADS_ACHADO_MAIOR}
🔬 ${REGRA_BIOPSIA_CRITERIO}
🔬 EXTENSÃO DA RESSECÇÃO: a quantidade de segmentos que a ressecção exigiria é JULGAMENTO CIRÚRGICO, não dado do exame. NUNCA a apresente como "documentada" nem a deduza sozinho a partir da localização das lesões. Só a inclua se houver, NOS DADOS, uma avaliação cirúrgica explícita — e então atribua a ela ("em avaliação da equipe cirúrgica, ..."). Na dúvida, OMITA o critério.
⛔ AUSÊNCIA NÃO DESCRITA (B367): NUNCA afirme que algo está ausente se o laudo do exame simplesmente não menciona aquilo. "Sem linfonodomegalias", "sem metástases", "sem invasão vascular" só podem ser escritos se o exame declarar. Se o exame é silente, OMITA a frase — não a transforme em negativa.
⚠️ PRECEDÊNCIA REGULATÓRIA (CRÍTICO — LER): nesta seção, MELD > 10, Child B/C e sódio < 135 mEq/L são GATILHOS REGULATÓRIOS FORMAIS de irressecabilidade, não achados clínicos a ponderar. Para o critério "MELD > 10", use o MELD-Na DA INSCRIÇÃO quando ele constar nos FATOS VERIFICADOS. Se o MELD-Na da inscrição NÃO constar, use o "MELD do exame" que os FATOS VERIFICADOS trouxerem, citando o valor JUNTO COM A DATA do exame (ex.: "MELD 12, de 20/07/2026") e chamando-o apenas de "MELD" — NUNCA de "MELD-Na" nem de "MELD-Na da inscrição" (é outro escore; escrever esse rótulo afirma um número que não foi calculado). Se os FATOS VERIFICADOS não trouxerem NENHUM MELD, OMITA o critério de MELD — não o busque em outra parte dos dados. Neste laudo, não calcule, recalcule nem estime MELD: use exatamente o número dos FATOS VERIFICADOS. Para os valores laboratoriais (sódio etc.), use EXATAMENTE os do bloco "CRITÉRIOS DE IRRESSECABILIDADE — EXAME DO MELD VÁLIDO DA INSCRIÇÃO" dos FATOS VERIFICADOS (exame do MELD válido da inscrição), NÃO os de "LABORATÓRIO MAIS RECENTE": uma melhora laboratorial posterior NÃO anula um critério já atendido enquanto a inscrição vige. Em especial, sódio < 135 mEq/L NÃO é "hiponatremia limítrofe a ignorar": se esse sódio for < 135, você DEVE listá-lo como critério atendido (valor + data). A regra clínica geral de não dramatizar valores limítrofes / próximos da referência NÃO se aplica a estes critérios regulatórios — aqui o limiar é o da norma (135), não o de gravidade clínica.
🔒 OBRIGATÓRIO (não opcional): TODO critério marcado como "critério ATENDIDO" no bloco "CRITÉRIOS DE IRRESSECABILIDADE — EXAME DO MELD VÁLIDO DA INSCRIÇÃO" dos FATOS VERIFICADOS DEVE ser listado nesta seção, com valor e data. NÃO basta citar um único critério: liste TODOS os que estão marcados ATENDIDO (ex.: se MELD-Na E sódio E hipertensão portal estão atendidos, os TRÊS devem constar). Omitir um critério marcado ATENDIDO reprova o laudo.
Se um critério NÃO estiver presente nos dados, OMITA aquele item — NÃO escreva placeholders como [VERIFICAR], que são bloqueados pelo sistema e reprovam o laudo. Mas NUNCA omita um critério que ESTÁ presente nos dados.
📭 SEÇÃO VAZIA QUANDO NÃO HOUVER NENHUM (B368 — IMPORTANTE): se NENHUM critério estiver sustentado pelos dados (nem MELD > 10, nem Child B/C, nem sódio < 135, nem hipertensão portal, nem um critério anatômico que o exame sustente), escreva o TÍTULO da seção e DEIXE-A SEM CONTEÚDO. Nesse caso: NÃO invente critério, NÃO force leitura anatômica do exame, NÃO escreva placeholder, e NÃO escreva frase alguma explicando a ausência ou se desculpando ("não foram identificados critérios", "dados insuficientes", "a avaliar") — o médico preenche esta seção na revisão. A seção em branco é o comportamento correto e esperado; texto de desculpa no lugar dela reprova o laudo. A mesma disciplina vale para a JUSTIFICATIVA CLÍNICA: se a seção de critérios ficou vazia, não se apoie em critério nenhum ali — sustente o pedido no CHC confirmado dentro dos Critérios de Milão e no fígado cirrótico.

**ESTADIAMENTO DE EXTENSÃO**
Tomografia de tórax (citar data): resultado. Ausência de invasão vascular e metástases se confirmado.

**JUSTIFICATIVA CLÍNICA**
Parágrafo formal solicitando a concessão de Situação Especial. Cite: lesão confirmada ≥2cm LI-RADS 5 (ou LI-RADS 4 com AFP>100), dentro dos Critérios de Milão, irressecabilidade confirmada pelos critérios acima, e o transplante hepático como a MELHOR opção terapêutica curativa neste cenário — único tratamento que aborda simultaneamente o tumor e a cirrose de base. Ao dizer por que a ressecção está contraindicada, apoie-se EXATAMENTE nos critérios que você listou na seção anterior (ex.: a disfunção hepática expressa pelo MELD) — NÃO introduza aqui um motivo que não consta lá. NÃO escreva que o transplante é a "única" alternativa curativa: é linguagem absoluta e médico-legalmente frágil (existem outras modalidades de intenção curativa, como a ablação), que enfraquece o laudo — prefira "melhor opção curativa" justificando por que as demais não se aplicam a esta paciente.
⛔ NÃO AFIRME QUE O TRANSPLANTE ELIMINA A RECIDIVA (B371 — laudo real de 30/10): o carcinoma hepatocelular RECIDIVA após o transplante (da ordem de 10 a 15% mesmo dentro dos Critérios de Milão). São PROIBIDAS, em qualquer seção, frases como "elimina o risco de recidiva", "eliminação do risco de recidiva", "sem risco de recidiva", "cura definitiva", "erradicação do risco" e equivalentes — além de falsas, são linguagem absoluta que o revisor derruba. Se quiser comparar, diga "MENOR risco de recidiva que a ressecção ou a ablação em fígado cirrótico", que é verdadeiro e suficiente. O argumento forte já é outro: o transplante trata o tumor E a cirrose de base no mesmo ato.

IMPORTANTE: Cite a data de TODOS os exames mencionados. Seja preciso com valores numéricos. Não invente dados — se a data não estiver disponível, OMITA a menção à data ou reformule a frase, NUNCA escreva placeholders como [COMPLETAR DATA], que são bloqueados pelo sistema e reprovam o laudo.
⛔ NÃO COMENTE A QUALIDADE DOS DADOS DENTRO DO LAUDO. Proibidas observações do tipo "valor inconsistente", "artefato laboratorial", "provável erro de digitação", "dado não disponível no prontuário", "registro divergente". Este documento vai à Câmara Técnica, não é auditoria interna: expor fragilidade do próprio registro enfraquece o pedido. Se um valor parecer inconsistente, simplesmente NÃO o use e NÃO o mencione — escolha outro dado da mesma natureza ou omita a frase. (O sistema tem canais próprios para sinalizar isso ao médico, fora do documento.)
⛔ VALE PARA O LAUDO INTEIRO (todas as seções, inclusive a JUSTIFICATIVA CLÍNICA): nunca explique nem justifique o método diagnóstico do CHC. O diagnóstico por LI-RADS 5 é AFIRMADO, não argumentado — proibidas, em qualquer parte do texto, frases sobre "critérios de imagem", "dispensa de biópsia", "confirmação histológica desnecessária" ou "método não invasivo". ⚠️ EXCEÇÃO: citar um laudo anatomopatológico que EXISTE não é explicar método — é apresentar a prova, e é OBRIGATÓRIO (regra de biópsia).
Seja conciso — máximo 400 palavras no total.`;
  };

  const laudosIA = [
    {
      id: "comite_tx",
      label: "Relatório para Comitê de Transplante",
      instrucao: `Gere um relatório clínico completo para o comitê de transplante hepático. Siga esta estrutura:

      REGRAS OBRIGATÓRIAS:
      ${REGRA_MELD_30}
      ${REGRA_TERMINOLOGIA}
      ${REGRA_ETIOLOGIA_NAO_INFERIDA}

**DIAGNÓSTICO DE BASE**
Diagnóstico principal com CID. NÃO mencione "situação especial" aqui.
**INDICAÇÃO DE TRANSPLANTE**
Descreva a indicação clínica com dados objetivos (MELD, Child-Pugh, complicações). NÃO recalcular o MELD.
**SITUAÇÃO CLÍNICA ATUAL**
MELD atual com data, Child-Pugh com pontuação, exames laboratoriais recentes com valores.
**COMORBIDADES RELEVANTES**
Liste comorbidades documentadas nos dados do paciente.
**EXAMES DE IMAGEM**
Resuma os exames mais recentes com datas e achados principais.
**SITUAÇÃO EM LISTA**
Busque o campo "numeroRGCT" dentro de "inscricaoTx" nos dados e cite como "RGCT nº [valor]".
Para situação especial: verifique o campo "situacaoEspecial" dentro de "inscricaoTx". APENAS se esse campo for explicitamente true E houver "dataAprovacaoSE" preenchida, escreva "Situação especial aprovada em [dataAprovacaoSE]". Se o campo for false, ausente ou sem data, escreva "Sem situação especial ativa no momento". NUNCA infira situação especial a partir dos diagnósticos clínicos ou presença de CHC.
**PARECER**
Conclusão sobre elegibilidade, pendências pré-transplante e próximos passos.
Seja conciso — máximo 400 palavras no total.`,
    },
    {
      id: "evolucao",
      label: "Laudo de Evolução Clínica",
      instrucao: `Gere um laudo de evolução clínica resumindo o acompanhamento do paciente. Siga estas regras OBRIGATÓRIAS:

      REGRAS CRÍTICAS DE FIDELIDADE:
      ${REGRA_FIDELIDADE}
      ${REGRA_ETIOLOGIA_NAO_INFERIDA}
      ${REGRA_TERMINOLOGIA}
      - NÃO faça diagnósticos ou inferências clínicas além do que está explicitamente registrado nas hipóteses diagnósticas do prontuário. Se as hipóteses estão em aberto, cite-as como tal: "hipóteses diagnósticas em investigação"

ESTRUTURA:
**EVOLUÇÃO CLÍNICA**
Resumo do contexto clínico atual.

**HISTÓRICO DOCUMENTADO**
Cite apenas eventos e consultas efetivamente registrados no prontuário, com datas reais.

**EXAMES MAIS RECENTES**
Valores laboratoriais com datas.

**IMPRESSÃO CLÍNICA E HIPÓTESES DIAGNÓSTICAS**
Busque o campo "hipoteses" ou "hipotesesDiagnosticas" dentro de cada consulta registrada e liste-as EXATAMENTE como estão escritas no prontuário. Formato: "Hipóteses diagnósticas registradas: [copiar exatamente]". Se estiverem em aberto, acrescente "— em investigação". NÃO omita e NÃO reformule as hipóteses registradas.

**DADOS DO TRANSPLANTE**
Se houver retransplante registrado, cite o motivo do retransplante conforme consta nos dados do transplante (campo "motivo" ou "motivoRetransplante"). Inclua data e indicação.

**PLANO TERAPÊUTICO**
Condutas indicadas.

IMPORTANTE: Se há hipóteses diagnósticas registradas nas consultas, elas DEVEM aparecer no laudo. Não omita informações clínicas documentadas em nome de brevidade.

REGRA FINAL (PRIORIDADE MÁXIMA): O MELD deste prontuário JÁ É MELD 3.0 — NÃO recalcular como MELD-Na nem estimar valores diferentes. "Caso negado" por um serviço de transplante NÃO é "indeferido pela Central de Transplantes" — usar EXATAMENTE a terminologia do prontuário. Usar "pendências pré-transplante" em vez de "clearance cirúrgico".

Seja conciso — máximo 400 palavras no total.`,
    },
    {
      id: "situacao_especial",
      label: "Situação Especial (CHC)",
      instrucao: instrucaoSituacaoEspecial(),
    },
    {
      id: "inss_auxilio",
      label: "Laudo para INSS — Auxílio-Doença",
      instrucao: `Gere um laudo médico CONCISO para fins de Auxílio-Doença junto ao INSS. O laudo deve caber em UMA PÁGINA.

FINALIDADE: Auxílio-Doença (avaliação de incapacidade laborativa TEMPORÁRIA) — INSS.

${REGRAS_LAUDO_PREVIDENCIARIO}

PRINCÍPIO PERICIAL (igual aos demais laudos de INSS — AI_118): o médico assistente DESCREVE os fatos clínicos; a caracterização de incapacidade e a definição do período de afastamento são EXCLUSIVAS do perito do INSS. NÃO cravar incapacidade, NÃO estipular prazo, NÃO recomendar/solicitar a concessão — atenha-se aos fatos clínicos documentados.

Siga EXATAMENTE esta estrutura:

**DIAGNÓSTICOS PRINCIPAIS**
Liste os diagnósticos numerados com CID-10. Apenas diagnósticos relevantes para o quadro.

**HISTÓRICO E QUADRO CLÍNICO ATUAL**
Dois parágrafos. (1º) Diagnóstico, progressão e situação clínica atual, com os dados objetivos relevantes (MELD, Child-Pugh, laboratoriais principais, achados de imagem) integrados na narrativa — NÃO listar exames separadamente. (2º) COMPROMETIMENTO FUNCIONAL — descreva de forma CONCRETA e fundamentada como as complicações DOCUMENTADAS repercutem na capacidade laboral: para cada limitação relevante, explicite o NEXO entre o achado clínico e a restrição (ex.: fadiga limitante pela anemia; ascite volumosa limitando mobilidade; encefalopatia comprometendo cognição e atenção; dependência de transfusões/procedimentos seriados e internações recorrentes que, por si, impõem afastamentos periódicos). CALIBRE A ÊNFASE PELO EIXO DOMINANTE DA GRAVIDADE: quando a condição principal for uma NEOPLASIA MALIGNA ativa/avançada (ex.: carcinoma hepatocelular em progressão, multifocal ou FORA dos critérios de Milão), o comprometimento funcional deve ancorar PRIMARIAMENTE na gravidade oncológica — doença maligna em atividade e progressão documentada; "fora de Milão" compromete a via curativa por transplante, exigindo downstaging de resultado incerto — e no ÔNUS do próprio tratamento oncológico (terapia sistêmica e seus efeitos adversos, procedimentos invasivos diagnósticos e terapêuticos seriados, fadiga relacionada à doença). NÃO reduza a limitação a "disponibilidade para comparecer a consultas/exames" (logística de comparecimento), que subdimensiona a gravidade real. Faça os fatos clínicos PESAREM com linguagem objetiva e precisa — SEM adjetivos dramáticos, SEM estimar sobrevida, SEM cravar incapacidade e SEM inventar descompensação que não exista (paciente Child A compensado, sem ascite/encefalopatia, permanece descrito como tal; a gravidade, nesse caso, é oncológica, não de insuficiência hepática). Descreva APENAS limitações com base documentada — NÃO inventar nem citar limitações não documentadas. NÃO transforme achados laboratoriais isolados sem repercussão funcional (plaquetopenia, anemia leve, leucopenia) em limitações: no cirrótico a plaquetopenia é marcador de hipertensão portal/hiperesplenismo (hemostasia rebalanceada), não risco hemorrágico ao esforço — só cite sangramento como limitação se houver hemorragia varicosa DOCUMENTADA.

**CONCLUSÃO**
Um único parágrafo que: (1) resume o prognóstico em uma frase; (2) apresenta os FATOS CLÍNICOS e as LIMITAÇÕES FUNCIONAIS documentadas que fundamentam a gravidade; (3) DEIXA a caracterização de incapacidade e a definição do período de afastamento à perícia do INSS — sem afirmar incapacidade, sem cravar prazo, sem solicitar a concessão. Use este modelo adaptando os dados:
"O prognóstico depende de [tratamento / resposta terapêutica / etc.]. O quadro clínico descrito — [diagnósticos com etiologia e complicações documentadas] — cursa com [limitações funcionais concretas documentadas] e necessita de acompanhamento especializado contínuo. A caracterização de eventual incapacidade laborativa e a definição do período de afastamento, para fins de Auxílio-Doença, competem à perícia médica do INSS, à luz dos elementos clínicos aqui documentados."

PROIBIDO: afirmar que o segurado "apresenta incapacidade" (de qualquer tipo); cravar prazo ("por prazo indeterminado", "por X dias/meses"); recomendar ou solicitar a concessão do benefício; "incapacidade definitiva/permanente", "aposentadoria por invalidez"; estimativa de sobrevida em meses/anos — essas determinações são exclusivas do perito do INSS.

IMPORTANTE: Use apenas dados reais presentes no prontuário. Se um dado não estiver disponível, OMITA a frase ou reformule sem o dado — NUNCA escreva placeholders como [COMPLETAR], que são bloqueados pelo sistema e reprovam o laudo. NÃO criar seções separadas de exames, tratamento ou prognóstico, nem subtítulo próprio para o comprometimento funcional (ele é o 2º parágrafo do HISTÓRICO). Mantenha EXATAMENTE as 3 seções em negrito (DIAGNÓSTICOS PRINCIPAIS, HISTÓRICO E QUADRO CLÍNICO ATUAL, CONCLUSÃO).
LIMITE: até ~350 palavras — o laudo DEVE caber em UMA página; use o espaço para FUNDAMENTAR o comprometimento funcional, não para listar exames.`,
    },
    {
      id: "inss_aposentadoria",
      label: "Laudo para INSS — Aposentadoria por Incapacidade Permanente",
      instrucao: `Você é um hepatologista especialista em transplante hepático gerando um laudo médico formal para fins previdenciários.

FINALIDADE: Aposentadoria por Incapacidade Permanente — INSS (EC 103/2019 / Lei 8.213/91 atualizada)

${REGRAS_LAUDO_PREVIDENCIARIO}
8. O critério legal vigente (EC 103/2019) é INCAPACIDADE TOTAL E PERMANENTE PARA QUALQUER ATIVIDADE LABORATIVA — nunca escreva "para atividade habitual" (critério revogado).
9. Nunca use os termos: "temporária", "transitória", "poderá se recuperar", "incapacidade parcial".
10. Nunca escreva "Recomenda-se a concessão do benefício" — essa decisão é exclusiva do perito do INSS.

CRITÉRIOS QUE JUSTIFICAM INCAPACIDADE TOTAL (cite apenas os que estiverem DOCUMENTADOS no prontuário):
- Encefalopatia hepática documentada (qualquer grau): compromete cognição e coordenação, impedindo qualquer atividade
- Ascite refratária ou de repetição: limitação física permanente e risco infeccioso contínuo
- Hemorragia varicosa prévia com coagulopatia (INR elevado): risco hemorrágico em qualquer esforço físico
- Child B ≥ 8 pontos ou MELD 3.0 ≥ 15: disfunção hepática grave com repercussão multissistêmica
- Child C (qualquer MELD): insuficiência hepática avançada com comprometimento global
- Inscrição ativa em lista de espera para transplante hepático com RGCT: o próprio sistema público de saúde reconhece formalmente a gravidade da condição como incompatível com vida laboral normal
- Pós-transplante com rejeição crônica e disfunção progressiva do enxerto: equivalente funcional a recirrose
- Pós-transplante com colangiopatia isquêmica e complicações recorrentes: internações e procedimentos repetidos
- Recidiva de CHC pós-transplante sem opção curativa: doença oncológica ativa sobre enxerto funcionante
ATENÇÃO: Pós-transplante estável, com enxerto funcionante, sem rejeição crônica e sem complicações documentadas, geralmente NÃO configura incapacidade total e permanente para qualquer atividade laborativa. Nesse caso, descreva objetivamente a situação clínica sem afirmar incapacidade permanente.

ESTRUTURA OBRIGATÓRIA (máximo 1 página — três seções, sem adicionar outras):

**LAUDO MÉDICO**
**Finalidade: Aposentadoria por Incapacidade Permanente — INSS**

**1. Diagnóstico e Situação Clínica**
CID-10 e nome da doença com etiologia. Tempo de evolução baseado nos dados documentados. Situação atual com escore Child-Pugh (pontuação numérica obrigatória) e MELD 3.0. Se inscrito em lista de espera para transplante hepático, mencionar com RGCT. Se pós-transplante, descrever data, evolução e complicações documentadas. Comorbidades relevantes presentes no prontuário.

**2. Comprometimento Funcional**
Descrever objetivamente, com base exclusiva nos dados documentados, de que forma a doença impede o exercício de qualquer atividade laborativa. Especificar: quais complicações estão presentes, quais limitações funcionais concretas elas geram, e por que essas limitações têm caráter permanente dado o estágio da doença. Não mencionar limitações não documentadas.

**3. Conclusão**
Uma a duas frases. A conclusão deve apresentar os fatos clínicos que fundamentam a gravidade da condição e deixar a determinação formal de incapacidade ao perito. Modelo: "O quadro clínico descrito — [diagnóstico com etiologia e complicações documentadas] — configura comprometimento funcional de caráter crônico e grave, com repercussão substancial sobre a capacidade laborativa. A determinação quanto à incapacidade total e permanente para o exercício de qualquer atividade laborativa, nos termos da EC 103/2019, cabe ao perito médico do INSS, considerando os elementos clínicos aqui documentados." Sem estimativa de tempo. Sem recomendação de concessão de benefício.`,
    },
    {
      id: "isencao_ir",
      label: "Laudo para Isenção de Imposto de Renda",
      instrucao: `Você é um hepatologista especialista em transplante hepático gerando um laudo médico formal para fins tributários.

FINALIDADE: Isenção de Imposto de Renda sobre proventos de aposentadoria, reforma e pensão — Lei 7.713/88, Art. 6º, inciso XIV, Receita Federal do Brasil.

ENQUADRAMENTO LEGAL — ESCOLHA O CORRETO CONFORME OS DADOS DO PACIENTE:

OPÇÃO A — "HEPATOPATIA GRAVE" (usar quando não há CHC confirmado):
Aplicável quando há evidência documentada de pelo menos um dos seguintes:
- Cirrose hepática com complicação documentada (ascite, encefalopatia, hemorragia varicosa, peritonite bacteriana espontânea)
- Child-Pugh B com pontuação ≥ 8 ou Child-Pugh C
- MELD 3.0 ≥ 15
- Inscrição ativa em lista de espera para transplante hepático
- Pós-transplante com complicação grave documentada (rejeição crônica, colangiopatia isquêmica, recidiva viral com fibrose avançada)
ATENÇÃO: Pós-transplante estável sem complicações é juridicamente controverso para "hepatopatia grave" — nesse caso, use linguagem que descreva a cronicidade da condição e a necessidade de imunossupressão permanente, sem afirmar categoricamente a gravidade atual se ela não estiver documentada.

OPÇÃO B — "NEOPLASIA MALIGNA" (usar quando há CHC confirmado, mesmo pós-transplante):
Aplicável a qualquer paciente com diagnóstico confirmado de carcinoma hepatocelular (CID-10 C22.0), independentemente de estar em remissão ou pós-transplante. O STJ consolidou (Súmula 627) que a isenção por neoplasia maligna persiste mesmo após remissão ou cura. Este é o enquadramento mais sólido juridicamente para pacientes com CHC.
Quando aplicável, usar ambos os enquadramentos: "portador de hepatopatia grave (cirrose hepática) e de neoplasia maligna (carcinoma hepatocelular), ambas previstas no Art. 6º, XIV da Lei 7.713/88."

${REGRAS_LAUDO_PREVIDENCIARIO}
8. Nunca afirme que o paciente "tem direito à isenção" — o reconhecimento do direito é da Receita Federal ou do Judiciário.

ESTRUTURA OBRIGATÓRIA (máximo 1 página — três seções, sem adicionar outras):

**LAUDO MÉDICO**
**Finalidade: Isenção de Imposto de Renda — Lei 7.713/88, Art. 6º, XIV**

**1. Diagnóstico e Situação Clínica**
CID-10 e nome completo da doença com etiologia. Situação atual com escore Child-Pugh (pontuação numérica obrigatória) e MELD 3.0. Se CHC confirmado, mencionar explicitamente com CID-10 C22.0. Se inscrito em lista de espera para transplante hepático, mencionar com RGCT. Se pós-transplante, descrever data, regime de imunossupressão atual e complicações documentadas. Comorbidades relevantes presentes no prontuário.

**2. Enquadramento Legal**
Aplicar a Opção A, Opção B ou ambas conforme os dados do paciente (ver critérios acima). Redigir de forma direta: "O(A) paciente é portador(a) de [doença], enquadrada como [hepatopatia grave / neoplasia maligna] nos termos do Art. 6º, inciso XIV da Lei 7.713/88, com alterações posteriores." Se CHC com remissão pós-transplante, acrescentar: "O diagnóstico de neoplasia maligna persiste para fins legais independentemente do estado atual de remissão, conforme entendimento consolidado do Superior Tribunal de Justiça."

**3. Conclusão**
Uma frase objetiva confirmando a condição médica e sua compatibilidade com o enquadramento legal, sem declarar o direito à isenção. Modelo: "Atesto que o(a) paciente é portador(a) de [diagnóstico(s) com CID-10], condição(ões) enquadrada(s) no Art. 6º, inciso XIV da Lei 7.713/88, para os fins que se fizerem necessários junto à Receita Federal do Brasil."`,
    },
    {
      id: "passe_livre",
      label: "Laudo para Passe Livre",
      instrucao: `Você é um hepatologista especialista em transplante hepático gerando um laudo médico formal para fins de transporte.

FINALIDADE: Passe Livre Interestadual — Lei 8.899/94, regulamentada pelos Decretos 3.691/2000 e 5.934/2006.

CRITÉRIO LEGAL — LEIA ANTES DE REDIGIR:
O passe livre é concedido a PESSOA COM DEFICIÊNCIA comprovadamente carente, nos termos da Lei 13.146/2015 (Estatuto da Pessoa com Deficiência). O papel do médico é confirmar a condição de deficiência e os impedimentos funcionais — NÃO confirmar renda (avaliada pelo INSS/CNAS) e NÃO justificar necessidade de deslocamento (não é critério legal).

QUANDO O PACIENTE HEPATOLÓGICO SE ENQUADRA (cite apenas o que tiver evidência — escolha o impedimento REAL, não some justificativas):
- Pós-transplante hepático: a base USUAL e suficiente é a imunossupressão crônica e por tempo indeterminado, que impõe vulnerabilidade permanente a infecções e restrições de exposição. Para o paciente pós-Tx estável, em geral ESTE é o único impedimento a declarar — de forma BREVE.
- Encefalopatia hepática documentada (qualquer grau): comprometimento cognitivo crônico/recorrente.
- Limitação de mobilidade: ascite volumosa/de repetição, sarcopenia grave ou edema crônico COM limitação funcional documentada.
ATENÇÃO 1: Cirrose hepática compensada (Child A, sem ascite, sem encefalopatia, sem hemorragia varicosa, sem limitação funcional documentada) provavelmente NÃO configura deficiência — nesse caso, descreva apenas os dados objetivos, sem afirmar deficiência.
ATENÇÃO 2: NÃO transforme achados laboratoriais isolados SEM repercussão funcional (plaquetopenia leve/moderada, anemia leve, leucopenia leve) em impedimentos. Não invente nem amplifique riscos que os dados não sustentam.

${REGRAS_LAUDO_PREVIDENCIARIO}
9. Nunca afirme ou confirme renda familiar — essa avaliação é do INSS/CNAS, não do médico.
10. Nunca justifique "necessidade de deslocamento para tratamento" — não é requisito legal do passe livre.
11. SEJA CONCISO e objetivo. NÃO seja prolixo nem empilhe justificativas; declare o(s) impedimento(s) real(is) e pare.
12. PASSE LIVRE NÃO É LAUDO PERICIAL DO INSS: aqui NÃO há perito a quem devolver a decisão — o médico assistente ATESTA a deficiência e SOLICITA a concessão. É CORRETO atestar e pedir a concessão (a proibição de "fechamento pericial" dos laudos de INSS/aposentadoria/BPC NÃO se aplica a este laudo).

ESTRUTURA OBRIGATÓRIA (máximo 1 página — três seções, sem adicionar outras):

**LAUDO MÉDICO**
**Finalidade: Passe Livre Interestadual — Lei 8.899/94**

**1. Diagnóstico e Situação Clínica**
CID-10 e nome da doença com etiologia. Tempo de evolução documentado. Se PRÉ-transplante/cirrótico, incluir Child-Pugh (pontuação) e MELD 3.0. Se PÓS-transplante, NÃO usar MELD/Child (não se aplicam — a cirrose nativa não existe mais); descrever, de forma breve, a data do transplante, o regime de imunossupressão atual e a função do enxerto. Se inscrito em lista de espera, mencionar RGCT.

**2. Impedimento(s) Funcional(is) de Longo Prazo**
Em 2 a 4 frases, descreva objetivamente o(s) impedimento(s) REAL(is) e sua natureza crônica/de longo prazo. Use apenas os domínios com evidência documentada. Para o pós-Tx estável, baste a imunossupressão crônica (vulnerabilidade a infecções/restrições de exposição), de forma sucinta. NÃO mencionar necessidade de deslocamento. NÃO inflar achados laboratoriais menores.

**3. Conclusão**
Uma a duas frases que ATESTAM a condição e SOLICITAM a concessão. Modelo: "Atesto que o(a) paciente é portador(a) de [diagnóstico com CID-10], condição que impõe impedimentos de longo prazo à sua participação plena, e solicito a concessão do Passe Livre Interestadual, nos termos da Lei 8.899/94 e da Lei 13.146/2015." Sem estimativa de prognóstico temporal.`,
    },
    {
      id: "bpc_loas",
      label: "Laudo para BPC/LOAS",
      instrucao: `Você é um hepatologista especialista em transplante hepático gerando um laudo médico formal para fins assistenciais.

FINALIDADE: Benefício de Prestação Continuada — BPC/LOAS (Lei 8.742/93 atualizada pela Lei 13.146/2015)

DIFERENÇA CRÍTICA EM RELAÇÃO À APOSENTADORIA:
O BPC/LOAS não avalia incapacidade para o trabalho — avalia IMPEDIMENTOS DE LONGO PRAZO que, em interação com barreiras sociais e ambientais, restringem a participação plena do paciente na sociedade. Use a linguagem do modelo biopsicossocial (CIF — Classificação Internacional de Funcionalidade).

${REGRAS_LAUDO_PREVIDENCIARIO}
8. Nunca escreva "Recomenda-se a concessão do benefício" — decisão exclusiva do perito e assistente social do INSS.
9. Os impedimentos devem ser descritos como de longo prazo (mínimo 2 anos de evolução da doença, quando documentado).

DOMÍNIOS FUNCIONAIS CIF RELEVANTES EM HEPATOLOGIA (cite apenas os com evidência documentada):
- Funções mentais (b1): encefalopatia hepática — comprometimento de memória, atenção, orientação, raciocínio
- Funções cardiovasculares, hematológicas e respiratórias (b4): ascite volumosa com dispneia aos esforços mínimos, edema de membros, coagulopatia crônica (INR elevado) com risco hemorrágico permanente, plaquetopenia grave com restrição de atividades com risco de trauma
- Funções digestivas (b5): anorexia, náuseas, dor abdominal crônica documentada
- Funções neuromusculoesqueléticas (b7): sarcopenia hepática, fraqueza muscular, limitação de mobilidade
- Mobilidade e autocuidado (d4/d5): limitações nas atividades da vida diária por ascite, fraqueza ou encefalopatia
- Vida doméstica e participação social (d6/d9): restrições por risco de hemorragia, infecção bacteriana espontânea, episódios de encefalopatia

ESTRUTURA OBRIGATÓRIA (máximo 1 página — três seções, sem adicionar outras):

**LAUDO MÉDICO**
**Finalidade: Benefício de Prestação Continuada — BPC/LOAS**

**1. Diagnóstico e Situação Clínica**
CID-10 e nome da doença com etiologia. Tempo de evolução baseado nos dados documentados (destacar caráter de longo prazo quando ≥ 2 anos). Situação atual com escore Child-Pugh (pontuação numérica obrigatória) e MELD 3.0. Se inscrito em lista de espera para transplante hepático, mencionar com RGCT. Se pós-transplante, descrever data, evolução e complicações documentadas.

**2. Impedimentos Funcionais e Restrição de Participação**
Descrever objetivamente, com base nos dados documentados, quais impedimentos de natureza física ou mental a doença impõe ao paciente e de que forma esses impedimentos, em interação com as condições de vida, restringem sua participação plena nas atividades cotidianas e sociais. Usar os domínios CIF pertinentes ao caso (apenas os com evidência documentada). Destacar o caráter crônico e de longo prazo das limitações.

**3. Conclusão**
Uma a duas frases. Modelo: "O(A) paciente apresenta impedimentos de longo prazo decorrentes de [diagnóstico com etiologia e complicações documentadas], condição crônica que restringe substancialmente sua participação plena nas atividades da vida diária e social, nos termos da Lei 8.742/93 atualizada pela Lei 13.146/2015." Sem estimativa de prognóstico temporal. Sem recomendação de benefício.`,
    },
  ];

  // ============================================
  // VALIDAÇÃO — SITUAÇÃO ESPECIAL CHC
  // Portaria 8041/2025, Art. 139, II + critérios SP
  // ============================================
  function validarSituacaoEspecialCHC(dados: any): {
    elegivel: boolean;
    bloqueio?: string;
    avisos: string[];
    /** B334 — MELD do exame quando o da inscrição não foi digitado (null se não aplicável). */
    meldDoExame?: MeldDeExame | null;
  } {
    const avisos: string[] = [];

    // Parser de data tolerante a ISO (YYYY-MM-DD), BR (DD/MM/YYYY) e Timestamp
    // Firebase ({seconds}/{toDate}). Espelha normalizarDataParaISO de
    // anonimizarDados.ts — `new Date(str)` cru falha p/ formato BR (B217).
    const parseDataLocal = (d: any): Date | null => {
      if (!d) return null;
      if (typeof d === "object") {
        if (typeof d.seconds === "number") return new Date(d.seconds * 1000);
        if (typeof d.toDate === "function") return d.toDate();
      }
      const s = String(d).trim();
      const br = s.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
      const dt = br
        ? new Date(`${br[3]}-${br[2]}-${br[1]}T12:00:00`)
        : new Date(s);
      return isNaN(dt.getTime()) ? null : dt;
    };

    // ── GATE DE INSCRIÇÃO (B217, decisão médico-legal de Leonardo) ──
    // O laudo SE-CHC é pleito para paciente JÁ INSCRITO. O marcador real da
    // inscrição é o RGCT (Registro Geral da Central de Transplantes), cadastrado
    // na aba Avaliação Clínica. Considerar INSCRITO se houver RGCT OU data de
    // inscrição; bloquear apenas quando FALTAM OS DOIS (sem evidência de
    // inscrição). NÃO usar statusInscricao como trava — causou falso-bloqueio de
    // paciente inscrito (status pode vir "pre"/vazio mesmo com RGCT válido).
    const insc = dados.inscricaoTx || {};
    const temRGCT = !!String(insc.numeroRGCT || "").trim();
    const temDataInscricao = !!parseDataLocal(insc.dataInscricao);
    if (!temRGCT && !temDataInscricao) {
      return {
        elegivel: false,
        bloqueio:
          "Primeiro inscreva este paciente para gerar este laudo.\n\n" +
          "A Situação Especial por CHC é um pleito para paciente já inscrito na " +
          "lista de transplante. Cadastre a inscrição (RGCT e/ou data de inscrição) " +
          "na aba de Avaliação Clínica e gere o laudo em seguida.",
        avisos,
      };
    }

    // achadosImagem já tem os campos extraídos da IA, direto e ordenados
    const achadosImagem = dados.achadosImagem || [];
    const examesImagem = dados.imagens || dados.examesImagem || [];
    let temLesaoElegivel = false;
    let textoCompleto = "";

    // AFP — valor com vírgula brasileira convertido para float
    const afpRaw = dados.marcadores?.[0]?.afp ?? "0";
    const afp = parseFloat(String(afpRaw).replace(",", ".")) || 0;

    // Primeiro: varrer achadosImagem (mais confiável — já processado pela IA)
    for (const achado of achadosImagem) {
      const lesoesTexto = String(achado.lesoesHepaticas || "").toLowerCase();
      const conclusao = String(achado.conclusao || "").toLowerCase();
      const resumo = String(achado.resumoAchados || "").toLowerCase();
      // ✅ Bloco 1 AI_34: incluir literal do laudista na busca textual
      const literal = String(
        achado.conclusaoLaudistaLiteral || ""
      ).toLowerCase();
      const textoExame = `${lesoesTexto} ${conclusao} ${resumo} ${literal}`;
      textoCompleto += ` ${textoExame}`;

      const temLiRads5 =
        textoExame.includes("li-rads 5") ||
        textoExame.includes("li-rads v") ||
        textoExame.includes("lirads 5") ||
        textoExame.includes("lirads v") ||
        textoExame.includes("li-rads5");

      const temLiRads4 =
        textoExame.includes("li-rads 4") ||
        textoExame.includes("li-rads iv") ||
        textoExame.includes("lirads 4") ||
        textoExame.includes("lirads iv");

      const matchesTamanho =
        textoExame.match(/(\d+[.,]\d+|\d+)\s*(?:x\s*[\d.,]+)?\s*cm/g) || [];

      for (const match of matchesTamanho) {
        const num = parseFloat(match.replace(",", "."));
        if (num >= 2.0 && (temLiRads5 || (temLiRads4 && afp > 100))) {
          temLesaoElegivel = true;
        }
      }

      // statusMilao DENTRO + lesão ≥ 2cm
      const statusMilao = String(achado.statusMilao || "").toUpperCase();
      if (
        statusMilao === "DENTRO" &&
        matchesTamanho.some((m) => parseFloat(m.replace(",", ".")) >= 2.0)
      ) {
        temLesaoElegivel = true;
      }
    }

    // Segundo: fallback nos metadados dos exames (descricao/laudo)
    if (!temLesaoElegivel) {
      for (const exame of examesImagem) {
        const laudoTexto = String(exame.descricao || "").toLowerCase();
        textoCompleto += ` ${laudoTexto}`;

        const temLiRads5 =
          laudoTexto.includes("li-rads 5") || laudoTexto.includes("lirads 5");
        const temLiRads4 =
          laudoTexto.includes("li-rads 4") || laudoTexto.includes("lirads 4");
        const matchesTamanho =
          laudoTexto.match(/(\d+[.,]\d+|\d+)\s*(?:x\s*[\d.,]+)?\s*cm/g) || [];

        for (const match of matchesTamanho) {
          const num = parseFloat(match.replace(",", "."));
          if (num >= 2.0 && (temLiRads5 || (temLiRads4 && afp > 100))) {
            temLesaoElegivel = true;
          }
        }
      }
    }

    if (!temLesaoElegivel) {
      return {
        elegivel: false,
        bloqueio:
          `Este paciente NÃO preenche critérios para Situação Especial por CHC conforme Portaria 8041/2025 (Art. 139, II).\n\n` +
          `O critério exige lesão ≥ 2,0 cm com LI-RADS 5 (ou LI-RADS 4 com AFP > 100 ng/mL).\n\n` +
          `Nenhuma lesão com essas características foi identificada nos exames cadastrados. ` +
          `Verifique se os exames foram analisados pela IA e se os achados estão salvos.`,
        avisos,
      };
    }

    // Critérios de irressecabilidade (NT 01/2011-SP)
    // B217 (AI_101): coerência com a âncora do backend (fatosVerificados.js).
    //  - "MELD > 10" usa o MELD-Na DA INSCRIÇÃO (não MELD clássico nem 3.0).
    //  - Sódio/plaquetas: valor MAIS ALTERADO entre os exames AINDA dentro da
    //    SUA validade (exame vencido nunca conta), favorecendo o paciente — não
    //    o exame mais recente. A validade é função do MELD do próprio exame, de
    //    modo que captura o exame estabelecedor colhido ANTES do registro e
    //    rejeita exame de MELD alto mais antigo já vencido.
    const scores = dados.scores || {};
    const num = (x: any): number | null => {
      if (x === undefined || x === null || x === "") return null;
      const n = parseFloat(String(x).replace(",", "."));
      return isNaN(n) ? null : n;
    };

    // MELD-Na da inscrição é o número que conta. Fallback p/ scores.meld: muitos
    // cadastros gravam o MELD-Na (SP) no campo genérico `meld`, não em `meldNa`
    // (B217, diag prod — sem isso a validade-por-exame derruba o critério).
    let meldNa = num(scores.meldNa);
    if (meldNa === null) meldNa = num(scores.meld);
    // B334 (AI_149) — se o MELD da INSCRIÇÃO não foi digitado, cai para o MELD do
    // exame (o MESMO número que o médico vê na tela: gravado ou calculado dos 5
    // componentes pela fórmula única). Sem isto o critério "MELD > 10" não
    // disparava nunca em quem não preencheu o campo — achado ao vivo de Leonardo
    // (28/07: paciente com MELD 12 e o laudo dizendo "nenhum critério").
    const meldDoExame =
      meldNa === null
        ? meldMaisRecenteDeExames(
            (dados.examesLab || []) as any[],
            (dados as any)?.sexo === "F"
          )
        : null;
    const childPugh = String(
      scores.classeChild || scores.childPugh || ""
    ).toUpperCase();

    // Validade (dias) por exame, segundo o MELD daquele exame (valores.MELD;
    // fallback MELD-Na da inscrição): ≤10=365; 11–18=90; 19–25=30; >25=7.
    const validadeDias = (meld: number | null): number | null =>
      meld === null
        ? null
        : meld <= 10
        ? 365
        : meld <= 18
        ? 90
        : meld <= 25
        ? 30
        : 7;
    const agora = new Date();
    const exames = (dados.examesLab || []) as any[];
    const elegiveis = exames.filter((ex) => {
      const dt = parseDataLocal(ex?.data || ex?.dataColeta);
      if (!dt) return false;
      let meldEx = num(ex?.valores?.MELD);
      if (meldEx === null) meldEx = meldNa;
      // Sem MELD determinável: banda generosa (365d), não descartar o exame
      // (favor-paciente; o MELD-Na da inscrição não é campo estruturado — B217).
      const vd = validadeDias(meldEx) ?? 365;
      return dt.getTime() + vd * 86400000 >= agora.getTime();
    });
    // Entre os elegíveis, pega menor Na e menor plaquetas (favorece o paciente).
    let sodio: number | null = null;
    let plaquetas: number | null = null;
    for (const ex of elegiveis) {
      const v = ex?.valores || ex || {};
      const na = num(v.Sodio ?? v.sodio);
      if (na !== null && (sodio === null || na < sodio)) sodio = na;
      const plaq = num(v.Plaquetas ?? v.plaquetas);
      if (plaq !== null && (plaquetas === null || plaq < plaquetas))
        plaquetas = plaq;
    }

    const criterios: string[] = [];
    if (meldNa !== null && meldNa > 10) criterios.push(`MELD-Na ${meldNa} > 10`);
    else if (meldDoExame && meldDoExame.valor > 10) {
      criterios.push(
        `MELD ${meldDoExame.valor} > 10 (exame de ${formatarDataBRMeld(
          meldDoExame.data
        )})`
      );
      // B337 — algum componente do MELD tem valor implausível (erro de digitação):
      // o score que virará critério regulatório pode estar distorcido. Caso real
      // (28/07): albumina "0,1 g/dL" travada no piso 1,5 inflou o MELD ~3 pontos.
      if (meldDoExame.componentesSuspeitos.length > 0) {
        avisos.push(
          `⚠️ O MELD ${meldDoExame.valor} (exame de ${formatarDataBRMeld(
            meldDoExame.data
          )}) foi calculado com valor(es) fora da faixa plausível: ` +
            `${meldDoExame.componentesSuspeitos.join(", ")}.\n\n` +
            `Provável erro de digitação — e isso DISTORCE o MELD, que aqui vira ` +
            `critério de irressecabilidade. Confira o exame (ou preencha o campo ` +
            `"MELD-Na da inscrição", que tem precedência) antes de enviar o laudo.\n\n` +
            `Deseja continuar mesmo assim?`
        );
      }
      // B334 — o exame que sustenta o critério está fora da própria validade
      // (faixa do MELD, NT 01/2011): serve de pista, não de afirmação. Mesma
      // disciplina que o bloco já aplica ao sódio de exame vencido.
      if (!meldDoExame.dentroDaValidade) {
        avisos.push(
          `⚠️ O critério "MELD > 10" está sendo ancorado no exame de ` +
            `${formatarDataBRMeld(meldDoExame.data)} (MELD ${meldDoExame.valor}), ` +
            `que já passou da validade para essa faixa de MELD.\n\n` +
            `O campo "MELD-Na da inscrição" (Avaliação Clínica) está vazio. ` +
            `Confirme o score vigente na Central antes de enviar o laudo — ` +
            `preencher aquele campo torna o critério exato.\n\n` +
            `Deseja continuar mesmo assim?`
        );
      }
    }
    if (childPugh.includes("B") || childPugh.includes("C"))
      criterios.push(`Child-Pugh ${childPugh}`);
    if (sodio !== null && sodio < 135)
      criterios.push(`Sódio ${String(sodio).replace(".", ",")} mEq/L < 135`);
    // B368 (AI_156): a hipertensão portal volta a contar como critério
    // detectado, sem distinguir instância. A conferência não antecipa a régua
    // da câmara — ela diz ao médico o que está DOCUMENTADO.
    const temCSPH =
      plaquetas !== null &&
      plaquetas < 150000 &&
      textoCompleto.includes("variz");
    if (temCSPH)
      criterios.push(
        `CSPH: varizes + plaquetas ${plaquetas?.toLocaleString("pt-BR")}/mm³`
      );

    if (criterios.length === 0) {
      // B334 — o aviso passa a dizer o que EXATAMENTE falta (antes era uma lista
      // genérica que não ajudava a agir).
      const pistaMeld =
        meldNa === null && meldDoExame
          ? `\n\nObs.: o campo "MELD-Na da inscrição" (Avaliação Clínica) está vazio; ` +
            `o MELD do exame de ${formatarDataBRMeld(meldDoExame.data)} é ${
              meldDoExame.valor
            } (não atende ao > 10).`
          : meldNa === null
          ? `\n\nObs.: o campo "MELD-Na da inscrição" (Avaliação Clínica) está vazio e ` +
            `não há exame com os componentes do MELD (creatinina, bilirrubina, INR, sódio e albumina).`
          : "";
      // B367 (AI_156): a mensagem dizia "nenhum critério identificado" — o
      // médico lia "você não tem critério". O sistema só consegue conferir os
      // critérios que vêm de VALOR MEDIDO; os anatômicos dependem da leitura do
      // exame e nunca serão detectados sozinhos. Dizer o que foi conferido, e o
      // que não dá para conferir, é diferente de dizer que não há critério.
      const conferidos =
        "MELD > 10, Child-Pugh B/C, sódio < 135 e hipertensão portal (varizes + plaquetas)";
      // B367-FU2 (AI_156, correção de Leonardo): a frase anterior dizia que o
      // sistema "não tem como ver" os critérios anatômicos — e isso é falso: o
      // exame de imagem É lido e analisado, e o laudo recebe essa análise. O que
      // a conferência automática não faz é DECIDIR o critério (lesão que cruza a
      // fronteira de dois segmentos; extensão da ressecção é julgamento
      // cirúrgico). Então em vez de alegar cegueira, mostra-se o que a análise
      // já feita diz: os segmentos citados.
      const segs = segmentosCitados(textoCompleto);
      const dependemDeVoce =
        `• Ressecção de > 2 segmentos\n• Tumores em segmentos distintos\n\n` +
        (segs.length > 0
          ? `Na análise de imagem já feita, os segmentos citados são: ${listarSegmentos(
              segs
            )}.`
          : `A análise de imagem já feita não cita segmento hepático de forma identificável — confira o laudo do exame.`);
      avisos.push(
        `⚠️ Nenhum dos critérios que o sistema CONSEGUE conferir foi atendido.\n\n` +
          `Conferidos automaticamente (nenhum atendido): ${conferidos}.\n\n` +
          `A conferência automática não DECIDE os dois critérios anatômicos — eles dependem da sua leitura do exame:\n` +
          dependemDeVoce +
          pistaMeld +
          `\n\nSe algum deles se aplica, siga: o laudo RECEBE a análise de imagem já feita e vai citar o achado que sustenta o critério.\n\n` +
          `Se nenhum se aplica, siga também: o laudo deixa esta seção EM BRANCO para você escrever na revisão — ele não vai inventar critério nem se desculpar no texto.\n\nDeseja continuar mesmo assim?`
      );
    }

    // B334 — devolve o MELD do exame para o pedido do laudo ancorar o critério
    // (só quando o MELD da inscrição não foi digitado).
    return { elegivel: true, avisos, meldDoExame };
  }

  const gerarLaudoIA = async (laudo: (typeof laudosIA)[0]) => {
    setLoadingIA(true);
    setTipoLaudoIA(laudo.id);
    try {
      const dados = await coletarDadosCompletos(paciente);

      // Validação específica para Situação Especial CHC
      // B334 (AI_149): guarda o MELD do exame para ancorar o critério no laudo
      // quando o campo "MELD-Na da inscrição" está vazio.
      let meldDoExameSeChc: MeldDeExame | null = null;
      if (laudo.id.startsWith("situacao_especial")) {
        const validacao = validarSituacaoEspecialCHC(dados);
        if (!validacao.elegivel) {
          setLoadingIA(false);
          setTipoLaudoIA("");
          alert(validacao.bloqueio);
          return;
        }
        meldDoExameSeChc = validacao.meldDoExame || null;
        if (validacao.avisos.length > 0) {
          const continuar = window.confirm(validacao.avisos.join("\n\n"));
          if (!continuar) {
            setLoadingIA(false);
            setTipoLaudoIA("");
            return;
          }
        }
      }

      // Extrair RGCT diretamente dos dados antes de montar o prompt
      // Fallback: dados anonimizados → inscricaoTx legado → campos planos
      const rgctNumero =
        dados.inscricaoTx?.numeroRGCT ||
        paciente.inscricaoTx?.numeroRGCT ||
        paciente.numeroRGCT ||
        paciente.rgct ||
        null;
      const situacaoEspecial =
        paciente.inscricaoTx?.situacaoEspecial === true ||
        paciente.situacaoEspecial === true;
      const dataAprovacaoSE =
        paciente.inscricaoTx?.dataAprovacaoSE ||
        paciente.dataAprovacaoSE ||
        null;
      const statusSE =
        situacaoEspecial && dataAprovacaoSE
          ? `Situação especial APROVADA em ${dataAprovacaoSE}`
          : situacaoEspecial
          ? `Situação especial solicitada (data de aprovação não informada)`
          : `Sem situação especial ativa`;

      // Extrair hipóteses diagnósticas de todas as consultas
      const hipotesesRegistradas = (dados.consultas || [])
        .map((c: any) => c.hipoteses || c.hipotesesDiagnosticas)
        .filter((h: any) => h && String(h).trim().length > 0)
        .join(" | ");

      // ═══════════════════════════════════════════════════════════
      // [AI_55 Patch 2.2] Janela temporal escalável para laudos
      // ═══════════════════════════════════════════════════════════
      // Substitui o despejo bruto de TODAS as imagens e marcadores no
      // JSON.stringify por uma janela filtrada (replicando o padrão
      // validado em gerarPromptEvolucao — AI_53):
      //   - Paciente com Tx: pós-Tx integral + 5 últimas pré-Tx
      //     (imagens) e + 3 últimos pré-Tx (marcadores)
      //   - Paciente sem Tx: últimos 12 meses, com piso de segurança
      //     (10 imagens / 5 marcadores mais recentes se filtro reduzir)
      // Resto de "dados" (transplantes, consultas, sorologias, scores)
      // segue intacto — são pequenos e clinicamente sempre relevantes.
      // ═══════════════════════════════════════════════════════════
      // [AI_55 Patch 2.2.3] Detecção robusta de data de Tx — 4 caminhos
      // Aceita ISO (AAAA-MM-DD) e BR (DD/MM/AAAA), parser local sem timezone
      const dataTxRaw =
        (dados as any)?.transplantes?.[
          ((dados as any)?.transplantes?.length || 1) - 1
        ]?.dataTransplante ||
        (dados as any)?.transplante?.dataTransplante ||
        (dados as any)?.dadosTransplante?.dataTransplante ||
        (dados as any)?.inscricaoTx?.dataTransplante;
      let dataTx: string | null = dataTxRaw || null;
      // Normaliza BR -> ISO se necessário
      if (typeof dataTxRaw === "string") {
        const matchBR = dataTxRaw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
        if (matchBR) {
          const [, d, m, a] = matchBR;
          dataTx = `${a}-${m.padStart(2, "0")}-${d.padStart(2, "0")}`;
        }
      }
      // Parser sem viés de timezone (specs 4.4)
      let txTime: number | null = null;
      if (dataTx) {
        const m = dataTx.match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (m) {
          txTime = new Date(
            parseInt(m[1], 10),
            parseInt(m[2], 10) - 1,
            parseInt(m[3], 10)
          ).getTime();
        }
      }
      const limitePreTxImagens = 5;
      const limitePreTxMarcadores = 3;

      const imagensTodas = Array.isArray((dados as any).achadosImagem)
        ? (dados as any).achadosImagem
        : [];
      const marcadoresTodos = Array.isArray((dados as any).marcadores)
        ? (dados as any).marcadores
        : [];

      let imagensFiltradas: any[] = imagensTodas;
      let marcadoresFiltrados: any[] = marcadoresTodos;

      if (txTime !== null && !isNaN(txTime)) {
        // Paciente transplantado: pós-Tx integral + N últimas pré-Tx
        // [AI_55 Patch 2.2.3] Captura local para preservar narrowing
        // dentro dos filter callbacks (comportamento conhecido do TS)
        const txTimeNum: number = txTime;
        const ordenadasDescImg = [...imagensTodas].sort((a, b) => {
          const dA = new Date(a.dataExame || "").getTime() || 0;
          const dB = new Date(b.dataExame || "").getTime() || 0;
          return dB - dA;
        });
        const posTxImg = ordenadasDescImg.filter((img) => {
          const t = new Date(img.dataExame || "").getTime();
          return !isNaN(t) && t >= txTimeNum;
        });
        const preTxImg = ordenadasDescImg
          .filter((img) => {
            const t = new Date(img.dataExame || "").getTime();
            return !isNaN(t) && t < txTimeNum;
          })
          .slice(0, limitePreTxImagens);
        imagensFiltradas = [...posTxImg, ...preTxImg];

        const ordenadosDescMarc = [...marcadoresTodos].sort((a, b) => {
          const dA = new Date(a.dataColeta || a.data || "").getTime() || 0;
          const dB = new Date(b.dataColeta || b.data || "").getTime() || 0;
          return dB - dA;
        });
        const posTxMarc = ordenadosDescMarc.filter((m) => {
          const t = new Date(m.dataColeta || m.data || "").getTime();
          return !isNaN(t) && t >= txTimeNum;
        });
        const preTxMarc = ordenadosDescMarc
          .filter((m) => {
            const t = new Date(m.dataColeta || m.data || "").getTime();
            return !isNaN(t) && t < txTimeNum;
          })
          .slice(0, limitePreTxMarcadores);
        marcadoresFiltrados = [...posTxMarc, ...preTxMarc];
      } else {
        // Paciente sem Tx: últimos 12 meses com piso de segurança
        const agora = Date.now();
        const umAno = 365 * 24 * 60 * 60 * 1000;
        const limiteAno = agora - umAno;

        const ordenadasDescImg = [...imagensTodas].sort((a, b) => {
          const dA = new Date(a.dataExame || "").getTime() || 0;
          const dB = new Date(b.dataExame || "").getTime() || 0;
          return dB - dA;
        });
        const ultimoAnoImg = ordenadasDescImg.filter((img) => {
          const t = new Date(img.dataExame || "").getTime();
          return !isNaN(t) && t >= limiteAno;
        });
        imagensFiltradas =
          ultimoAnoImg.length >= 5
            ? ultimoAnoImg
            : ordenadasDescImg.slice(0, 10);

        const ordenadosDescMarc = [...marcadoresTodos].sort((a, b) => {
          const dA = new Date(a.dataColeta || a.data || "").getTime() || 0;
          const dB = new Date(b.dataColeta || b.data || "").getTime() || 0;
          return dB - dA;
        });
        const ultimoAnoMarc = ordenadosDescMarc.filter((m) => {
          const t = new Date(m.dataColeta || m.data || "").getTime();
          return !isNaN(t) && t >= limiteAno;
        });
        marcadoresFiltrados =
          ultimoAnoMarc.length >= 3
            ? ultimoAnoMarc
            : ordenadosDescMarc.slice(0, 5);
      }

      const dadosFiltrados = {
        ...dados,
        achadosImagem: imagensFiltradas,
        marcadores: marcadoresFiltrados,
      };

      const txDataLog = txTime
        ? (() => {
            const d = new Date(txTime);
            return `${String(d.getDate()).padStart(2, "0")}/${String(
              d.getMonth() + 1
            ).padStart(2, "0")}/${d.getFullYear()}`;
          })()
        : "sem Tx";
      console.log(
        `📊 [TextoLivre Patch 2.2] Janela temporal: imagens ${imagensFiltradas.length}/${imagensTodas.length} | marcadores ${marcadoresFiltrados.length}/${marcadoresTodos.length} | tx=${txDataLog}`
      );

      const prompt = `
Você é um médico hepatologista redigindo um documento oficial.
Paciente: ${paciente.sexo === "M" ? "Masculino" : "Feminino"}, ${
        paciente.dataNascimento
          ? Math.floor(
              (new Date().getTime() -
                new Date(paciente.dataNascimento).getTime()) /
                (365.25 * 24 * 60 * 60 * 1000)
            )
          : "idade não informada"
      } anos.
CPF: ${paciente.cpf || "não informado"}
CNS: ${paciente.cns || "não informado"}
RGCT: ${rgctNumero || "não informado"}
SITUAÇÃO ESPECIAL: ${statusSE}
HIPÓTESES DIAGNÓSTICAS REGISTRADAS NAS CONSULTAS: ${
        hipotesesRegistradas || "não registradas"
      }

      DADOS CLÍNICOS DO PACIENTE:
      ${JSON.stringify(dadosFiltrados, null, 2).substring(0, 60000)}

ATENÇÃO SOBRE SITUAÇÃO ESPECIAL:
- Verifique o campo "statusInscricao" nos dados. Se estiver como "negada", "indeferida" ou similar, a paciente NÃO tem situação especial aprovada.
- Só afirme que tem situação especial se o status for explicitamente "aprovada" ou "ativa".
- Se a situação especial foi negada ou indeferida, mencione isso no laudo: "Situação especial solicitada e indeferida pela Central de Transplantes."

INSTRUÇÃO:
${laudo.instrucao}

REGRAS:
- Escreva em português formal, em terceira pessoa
- Use dados REAIS do paciente (não invente valores)
- Se um dado não está disponível, escreva "[COMPLETAR]" para o médico preencher
- NÃO inclua cabeçalho, identificação do paciente (nome, CPF, CNS, idade, sexo) ou assinatura — o sistema adiciona automaticamente no PDF
- NÃO comece com "IDENTIFICAÇÃO DO PACIENTE" — comece direto pelo conteúdo do laudo
- NÃO adicione seção separada de "CID-10:" — cada tipo de laudo define onde e como incluir os códigos CID
${REGRA_NUTRICAO}
- NÃO inclua cidade, local, data ou assinatura ao final do texto — o sistema adiciona automaticamente no rodapé do PDF
- O documento deve ser CONCISO, com no máximo 1 página impressa (aproximadamente 400 palavras). Seja objetivo e direto. Evite repetições entre seções. Priorize dados clínicos relevantes e elimine frases genéricas.
- Data do documento: ${new Date().toLocaleDateString("pt-BR")}

FORMATO OBRIGATÓRIO DA RESPOSTA:
- Responda APENAS com o texto do laudo, pronto para impressão.
- NÃO inclua análises preliminares, auditorias clínicas, revisões, raciocínio diagnóstico, notas internas ou qualquer texto antes ou depois do laudo.
- NÃO inclua blocos de "AUDITORIA", "REVISÃO", "PROBLEMAS IDENTIFICADOS" ou similares.
- Comece diretamente pelo título do laudo (ex.: "LAUDO MÉDICO") e termine na última linha do conteúdo clínico.
`;

      // B120 (AI_66) — Migrado para analyzeClient (auth Firebase ID Token)
      const data = await callAnalyze<{
        content: Array<{ type: string; text: string }>;
        verificacao?: {
          confianca: string;
          problemas?: string[];
          [key: string]: unknown;
        };
        _proveniencia?: Record<string, unknown>;
      }>({
        prompt,
        useThinking: true,
        tipo: laudo.id,
        // B334 (AI_149) — SÓ no laudo SE-CHC e SÓ quando o MELD da inscrição não
        // foi digitado: leva o MELD do exame (mesmo número da tela) para a âncora
        // do backend citar o critério com valor, data e origem. Cópia LOCAL: a
        // análise geral, o chat e os demais laudos seguem com o payload intacto.
        dadosPaciente: meldDoExameSeChc
          ? {
              ...dados,
              scores: {
                ...((dados as any).scores || {}),
                meldCalculado: meldDoExameSeChc.valor,
                meldCalculadoData: meldDoExameSeChc.data,
                meldCalculadoOrigem: meldDoExameSeChc.origem,
                meldCalculadoVencido: !meldDoExameSeChc.dentroDaValidade,
              },
            }
          : dados,
      });
      const texto = data.content
        .filter((c: any) => c.type === "text")
        .map((c: any) => c.text)
        .join("\n")
        .trim();

      // 🔍 Verificar se a self-verification rodou
      if (data.verificacao) {
        console.log(
          "🔍 SELF-VERIFICATION RESULTADO:",
          JSON.stringify(data.verificacao, null, 2)
        );
        console.log(`✅ Confiança: ${data.verificacao.confianca}`);
        if (
          data.verificacao.problemas &&
          data.verificacao.problemas.length > 0
        ) {
          console.log("⚠️ Problemas encontrados:", data.verificacao.problemas);
        }
      } else {
        console.log(
          "ℹ️ Self-verification não retornou resultado (tipo ou dadosPaciente não enviados?)"
        );
      }

      // Salvar resultado da verificação (se existir)
      setVerificacaoIA(data.verificacao || null);

      // 🏷️ [B27 Patch 2] Log da proveniência recebida do backend
      if (data._proveniencia) {
        console.log(
          "🏷️ [PATCH 2 — PROVENIÊNCIA DA IA] " +
            JSON.stringify(data._proveniencia, null, 2)
        );
      } else {
        console.log(
          "⚠️ [PATCH 2] Backend não retornou _proveniencia — verifique deploy."
        );
      }
      // [B177 / AI_84] Persiste a proveniencia: captura no state para
      // sobreviver ate o handleGerarPDF (clique separado do usuario)
      setProvenienciaIA(data._proveniencia || null);

      setTitulo(laudo.label);
      setConteudo(texto);
      setConteudoOriginalIA(texto);
      setLaudoIdIA(laudo.id);
    } catch (error: any) {
      console.error("Erro ao gerar laudo IA:", error);
      alert("Erro ao gerar laudo. Tente novamente.");
    } finally {
      setLoadingIA(false);
      setTipoLaudoIA("");
    }
  };

  const tiposDocumento: { label: string; textoPadrao?: string }[] = [
    { label: "Encaminhamento" },
    {
      label: "Atestado Médico",
      textoPadrao: `Atesto para os devidos fins que o(a) paciente ${
        paciente.nome
      }, CPF ${
        paciente.cpf || "_______________"
      }, esteve em consulta médica nesta data, necessitando de afastamento de suas atividades por ______ dia(s), a partir de ${new Date().toLocaleDateString(
        "pt-BR"
      )}.

CID: _______________`,
    },
    {
      label: "Declaração de Comparecimento",
      textoPadrao: `Declaro para os devidos fins que o(a) paciente ${
        paciente.nome
      }, CPF ${
        paciente.cpf || "_______________"
      }, compareceu a consulta médica neste serviço no dia ${new Date().toLocaleDateString(
        "pt-BR"
      )}, no período das _______ às _______ horas.`,
    },
    { label: "Declaração" },
    { label: "Relatório Médico" },
    { label: "Laudo" },
    { label: "Solicitação" },
    { label: "Outro" },
  ];

  // AI_118 — seletor de documentos agrupado por FINALIDADE (rótulos maiores):
  // Atestados e relatórios (sem IA) · Laudos médicos (IA) · Laudos
  // previdenciários (IA) · Outros. Laudos IA separados por id.
  const IDS_LAUDO_MEDICO = [
    "comite_tx",
    "evolucao",
    "situacao_especial",
  ];
  const laudosMedicos = laudosIA.filter((l) => IDS_LAUDO_MEDICO.includes(l.id));
  const laudosPrevidenciarios = laudosIA.filter(
    (l) => !IDS_LAUDO_MEDICO.includes(l.id)
  );
  const tiposAtestado = tiposDocumento.filter((t) => t.label !== "Outro");
  const tiposOutros = tiposDocumento.filter((t) => t.label === "Outro");

  const tituloGrupoDoc = (texto: string) => (
    <div
      style={{
        fontSize: "11.5px",
        fontWeight: 700,
        color: theme.gray[500],
        fontFamily: theme.fonts.sans,
        textTransform: "uppercase",
        letterSpacing: "0.3px",
        margin: "8px 0 4px",
      }}
    >
      {texto}
    </div>
  );

  const renderBotaoTipo = (tipo: (typeof tiposDocumento)[number]) => (
    <button
      key={tipo.label}
      onClick={() => selecionarTipo(tipo)}
      style={{
        padding: "5px 12px",
        background:
          titulo === tipo.label ? theme.primary[600] : theme.gray[100],
        color: titulo === tipo.label ? "white" : theme.gray[700],
        border: "none",
        borderRadius: theme.borderRadius.md,
        fontSize: "12.5px",
        fontWeight: "600",
        cursor: "pointer",
        fontFamily: theme.fonts.sans,
      }}
    >
      {tipo.label}
    </button>
  );

  const renderBotaoLaudoIA = (laudo: (typeof laudosIA)[number]) => (
    <button
      key={laudo.id}
      onClick={() => gerarLaudoIA(laudo)}
      disabled={loadingIA}
      style={{
        padding: "5px 10px",
        background:
          loadingIA && tipoLaudoIA === laudo.id ? theme.primary[400] : "white",
        color:
          loadingIA && tipoLaudoIA === laudo.id ? "white" : theme.primary[700],
        border: `1px solid ${theme.primary[300]}`,
        borderRadius: theme.borderRadius.sm,
        fontSize: "11.5px",
        fontWeight: "600",
        cursor: loadingIA ? "wait" : "pointer",
        fontFamily: theme.fonts.sans,
        opacity: loadingIA && tipoLaudoIA !== laudo.id ? 0.5 : 1,
        display: "flex",
        alignItems: "center",
        gap: "3px",
      }}
    >
      {loadingIA && tipoLaudoIA === laudo.id && (
        <Loader2 size={11} style={{ animation: "spin 1s linear infinite" }} />
      )}
      {laudo.label}
    </button>
  );

  const selecionarTipo = (tipo: { label: string; textoPadrao?: string }) => {
    setTitulo(tipo.label);
    if (tipo.textoPadrao && !conteudo.trim()) {
      setConteudo(tipo.textoPadrao);
    }
    setConteudoOriginalIA(null);
    setLaudoIdIA(null);
    setVerificacaoIA(null);
    setProvenienciaIA(null);
  };

  // B289 (AI_127): config do PDF (cabeçalho/rodapé), fonte única.
  const configTextoLivre = () => ({
    nomeAmbulatorio,
    nomeMedico: usuarioLogado?.nome || "Médico",
    crm: usuarioLogado?.registroProfissional
      ? `${usuarioLogado.registroProfissional.tipo} ${usuarioLogado.registroProfissional.numero} ${usuarioLogado.registroProfissional.uf}`
      : "",
    endereco,
    cidade,
  });

  // B289 (AI_127): payload salvo em "prescricoes", compartilhado entre a geração
  // de PDF e a assinatura digital (mesma estrutura → mesmo documento no histórico).
  const construirPayloadTextoLivre = (): Record<string, unknown> => ({
    pacienteId: paciente.id,
    tipo: "texto",
    // B92: discrimina laudo médico (ato privativo) de relatório
    // multidisciplinar — base da granularidade por categoria nas
    // security rules (B88). Docs antigos sem subtipo = laudo_medico.
    subtipo:
      usuarioLogado?.categoria === "medico"
        ? "laudo_medico"
        : "relatorio_multi",
    titulo: `${titulo} - ${paciente.nome}`,
    conteudo: { texto: conteudo },
    config: configTextoLivre(),
    criadoEm: serverTimestamp(),
    criadoPor: usuarioLogado?.id || "",
    criadoPorNome: usuarioLogado?.nome || "",
    _ia: {
      usouIA: !!laudoIdIA,
      laudoIdIA: laudoIdIA || null,
      textoFoiEditado:
        conteudoOriginalIA !== null && conteudoOriginalIA !== conteudo,
      verificacao: verificacaoIA || null,
      _proveniencia: provenienciaIA || null,
    },
  });

  // Limpa o editor após gerar/assinar.
  const limparTextoLivre = () => {
    setTitulo("");
    setConteudo("");
    setVerificacaoIA(null);
    setConteudoOriginalIA(null);
    setLaudoIdIA(null);
    setProvenienciaIA(null);
  };

  const handleGerarPDF = async () => {
    if (!titulo.trim() || !conteudo.trim()) {
      alert("Preencha o tipo/título e o conteúdo do documento.");
      return;
    }
    gerarPDFTextoLivre(paciente, titulo, conteudo, configTextoLivre());
    // Salvar no histórico
    try {
      await addDoc(collection(db, "prescricoes"), construirPayloadTextoLivre());
      if (onPrescricaoSalva) onPrescricaoSalva();
    } catch (error) {
      console.error("Erro ao salvar no histórico:", error);
    }
    limparTextoLivre();
  };

  // B289 (AI_127): assina digitalmente o Texto Livre (médico-only + flag). Monta
  // o MESMO HTML da impressão (com selo), salva o documento (captura o id para o
  // broker validar a autoria) e abre o ModalAssinatura.
  const handleAssinarTextoLivre = async () => {
    if (!titulo.trim() || !conteudo.trim()) {
      alert("Preencha o tipo/título e o conteúdo do documento.");
      return;
    }
    const registro = usuarioLogado?.registroProfissional
      ? `${usuarioLogado.registroProfissional.tipo} ${usuarioLogado.registroProfissional.numero} ${usuarioLogado.registroProfissional.uf}`
      : "";
    // B290 — QR de validação no rodapé (ao lado do selo grande ICP-Brasil).
    const codigo = gerarCodigoValidacao();
    const selo =
      montarSeloAssinaturaDigital({
        nome: usuarioLogado?.nome || "",
        registro,
        data: dataHoraSeloAgora(),
      }) + montarBlocoQR(codigo);
    const html = montarTextoLivreHTML(
      paciente,
      titulo,
      conteudo,
      configTextoLivre(),
      undefined,
      selo
    );

    let prescricaoId: string;
    try {
      const docRef = await addDoc(
        collection(db, "prescricoes"),
        construirPayloadTextoLivre()
      );
      prescricaoId = docRef.id;
      if (onPrescricaoSalva) onPrescricaoSalva();
    } catch (error) {
      console.error("Erro ao salvar o documento para assinatura:", error);
      alert("Não foi possível salvar o documento. Tente novamente.");
      return;
    }

    setModalAssinatura({
      html,
      tipo: "texto",
      prescricaoId,
      dados: {
        name: usuarioLogado?.nome || "",
        reason: titulo || "Texto Livre",
        location: cidade || "",
      },
      nomeArquivo: `${titulo || "documento"}-${paciente.nome}`.replace(
        /\s+/g,
        "_"
      ),
      codigoValidacao: codigo,
      dadosValidacao: {
        medicoNome: usuarioLogado?.nome || "",
        registro,
        pacienteTelefone: telefoneVigente(paciente),
        pacienteNascimento: paciente.dataNascimento || "",
      },
    });

    limparTextoLivre();
  };
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
      {/* CONFIG */}
      <div
        style={{
          background: theme.gray[50],
          borderRadius: theme.borderRadius.md,
          padding: "8px 12px",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            cursor: "pointer",
          }}
          onClick={() => setMostrarConfig((prev) => !prev)}
        >
          <span
            style={{
              fontSize: "12px",
              fontWeight: "600",
              color: theme.gray[600],
              fontFamily: theme.fonts.sans,
              display: "flex",
              alignItems: "center",
              gap: "4px",
            }}
          >
            <Settings size={12} /> Configuração do Documento
          </span>
          {mostrarConfig ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </div>
        {mostrarConfig && (
          <div
            style={{
              marginTop: "8px",
              display: "flex",
              flexDirection: "column",
              gap: "8px",
            }}
          >
            <div>
              <label
                style={{
                  fontSize: "11px",
                  fontWeight: "600",
                  color: theme.gray[500],
                  fontFamily: theme.fonts.sans,
                }}
              >
                Nome do Ambulatório
              </label>
              <input
                type="text"
                value={nomeAmbulatorio}
                onChange={(e) => {
                  marcarEdicaoConfig();
                  setNomeAmbulatorio(e.target.value);
                }}
                style={{
                  width: "100%",
                  padding: "6px 8px",
                  border: `1px solid ${theme.gray[300]}`,
                  borderRadius: theme.borderRadius.sm,
                  fontSize: "12px",
                  fontFamily: theme.fonts.sans,
                }}
              />
            </div>
            <div>
              <label
                style={{
                  fontSize: "11px",
                  fontWeight: "600",
                  color: theme.gray[500],
                  fontFamily: theme.fonts.sans,
                }}
              >
                Endereço
              </label>
              <input
                type="text"
                value={endereco}
                onChange={(e) => {
                  marcarEdicaoConfig();
                  setEndereco(e.target.value);
                }}
                style={{
                  width: "100%",
                  padding: "6px 8px",
                  border: `1px solid ${theme.gray[300]}`,
                  borderRadius: theme.borderRadius.sm,
                  fontSize: "12px",
                  fontFamily: theme.fonts.sans,
                }}
              />
            </div>
            <div style={{ display: "flex", gap: "16px" }}>
              <div style={{ flex: 1 }}>
                <label
                  style={{
                    fontSize: "11px",
                    fontWeight: "600",
                    color: theme.gray[500],
                    fontFamily: theme.fonts.sans,
                  }}
                >
                  Cidade e UF
                </label>
                <input
                  type="text"
                  value={cidade}
                  onChange={(e) => {
                  marcarEdicaoConfig();
                  setCidade(e.target.value);
                }}
                  style={{
                    width: "100%",
                    padding: "6px 8px",
                    border: `1px solid ${theme.gray[300]}`,
                    borderRadius: theme.borderRadius.sm,
                    fontSize: "12px",
                    fontFamily: theme.fonts.sans,
                  }}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label
                  style={{
                    fontSize: "11px",
                    fontWeight: "600",
                    color: theme.gray[500],
                    fontFamily: theme.fonts.sans,
                  }}
                >
                  Telefone
                </label>
                <input
                  type="text"
                  value={telefone}
                  onChange={(e) => {
                  marcarEdicaoConfig();
                  setTelefone(e.target.value);
                }}
                  style={{
                    width: "100%",
                    padding: "6px 8px",
                    border: `1px solid ${theme.gray[300]}`,
                    borderRadius: theme.borderRadius.sm,
                    fontSize: "12px",
                    fontFamily: theme.fonts.sans,
                  }}
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* TIPO DO DOCUMENTO */}
      <div>
        <label
          style={{
            fontSize: "12px",
            fontWeight: "600",
            color: theme.gray[700],
            fontFamily: theme.fonts.sans,
            display: "block",
            marginBottom: "4px",
          }}
        >
          <FileText size={14} style={{ marginRight: "4px" }} />
          Tipo / Título do Documento
        </label>
        {/* B252 (AI_114): os atalhos de tipo/título são MÉDICOS (Encaminhamento,
            Atestado, Laudo…). Para a enfermagem/multiprofissional eles não fazem
            sentido — fica só o campo de título personalizado abaixo. */}
        {ehMedico(usuarioLogado) && (
          <>
            {tituloGrupoDoc("Atestados e relatórios")}
            <div
              style={{
                display: "flex",
                gap: "6px",
                flexWrap: "wrap",
                marginBottom: "8px",
              }}
            >
              {tiposAtestado.map(renderBotaoTipo)}
            </div>
          </>
        )}
        {/* LAUDOS GERADOS POR IA — 🔐 B88: ato privativo do médico (espelho das
            firestore.rules e do gate no /api/analyze). AI_118: separados em
            "Laudos médicos" e "Laudos previdenciários" por finalidade. */}
        {ehMedico(usuarioLogado) && (
          <>
            {/* Grupo 2 — Laudos médicos (gerados com IA) */}
            <div
              style={{
                marginTop: "4px",
                marginBottom: "8px",
                padding: "10px",
                background: "linear-gradient(135deg, #eff6ff 0%, #f0fdf4 100%)",
                borderRadius: theme.borderRadius.md,
                border: "1px solid #bfdbfe",
              }}
            >
              <div
                style={{
                  fontSize: "11.5px",
                  fontWeight: 700,
                  color: theme.primary[700],
                  fontFamily: theme.fonts.sans,
                  marginBottom: "6px",
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                }}
              >
                <Bot size={13} /> Laudos médicos (gerados com IA)
              </div>
              <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                {laudosMedicos.map(renderBotaoLaudoIA)}
              </div>
            </div>

            {/* Grupo 3 — Laudos previdenciários (gerados com IA) */}
            <div
              style={{
                marginBottom: "8px",
                padding: "10px",
                background: "linear-gradient(135deg, #eff6ff 0%, #f0fdf4 100%)",
                borderRadius: theme.borderRadius.md,
                border: "1px solid #bfdbfe",
              }}
            >
              <div
                style={{
                  fontSize: "11.5px",
                  fontWeight: 700,
                  color: theme.primary[700],
                  fontFamily: theme.fonts.sans,
                  marginBottom: "6px",
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                }}
              >
                <Bot size={13} /> Laudos previdenciários (gerados com IA)
              </div>
              <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                {laudosPrevidenciarios.map(renderBotaoLaudoIA)}
              </div>
              {loadingIA && (
                <div
                  style={{
                    fontSize: "10px",
                    color: theme.primary[600],
                    marginTop: "6px",
                    fontFamily: theme.fonts.sans,
                  }}
                >
                  Gerando laudo com base nos dados do paciente... (pode levar até
                  30 segundos)
                </div>
              )}
            </div>

            {/* Grupo 4 — Outros */}
            {tiposOutros.length > 0 && (
              <>
                {tituloGrupoDoc("Outros")}
                <div
                  style={{
                    display: "flex",
                    gap: "6px",
                    flexWrap: "wrap",
                    marginBottom: "8px",
                  }}
                >
                  {tiposOutros.map(renderBotaoTipo)}
                </div>
              </>
            )}
          </>
        )}
        <input
          type="text"
          value={titulo}
          onChange={(e) => setTitulo(e.target.value)}
          placeholder="Ou digite um título personalizado..."
          style={{
            width: "100%",
            padding: "8px",
            border: `1px solid ${theme.gray[300]}`,
            borderRadius: theme.borderRadius.md,
            fontSize: "13px",
            fontFamily: theme.fonts.sans,
            boxSizing: "border-box",
          }}
        />
      </div>

      {/* CONTEÚDO */}
      <div>
        <label
          style={{
            fontSize: "12px",
            fontWeight: "600",
            color: theme.gray[700],
            fontFamily: theme.fonts.sans,
            display: "block",
            marginBottom: "4px",
          }}
        >
          Conteúdo
        </label>
        <textarea
          value={conteudo}
          onChange={(e) => setConteudo(e.target.value)}
          placeholder="Digite o conteúdo do documento..."
          rows={10}
          style={{
            width: "100%",
            padding: "12px",
            border: `1px solid ${theme.gray[300]}`,
            borderRadius: theme.borderRadius.md,
            fontSize: "13px",
            fontFamily: theme.fonts.sans,
            resize: "vertical",
            lineHeight: "1.6",
            boxSizing: "border-box",
          }}
        />
      </div>

      {/* SELO DE VERIFICAÇÃO DE FIDELIDADE */}
      {verificacaoIA && (
        <div
          style={{
            padding: "10px 14px",
            borderRadius: "6px",
            marginBottom: "12px",
            borderLeft: `4px solid ${
              verificacaoIA.confianca === "alta"
                ? "#16a34a"
                : verificacaoIA.confianca === "media"
                ? "#f59e0b"
                : "#dc2626"
            }`,
            backgroundColor:
              verificacaoIA.confianca === "alta"
                ? "#f0fdf4"
                : verificacaoIA.confianca === "media"
                ? "#fffbeb"
                : "#fef2f2",
            fontSize: "13px",
            fontFamily: theme.fonts.sans,
          }}
        >
          <div style={{ fontWeight: 600, marginBottom: "4px" }}>
            {/* B364 (AI_156): o selo não promete lista que não existe. Antes
                dizia "problemas identificados" mesmo com `problemas: []` —
                alarme vermelho sem nada abaixo (achado ao vivo num laudo de
                Auxílio-Doença). Rede de segurança: a causa foi corrigida no
                servidor, mas se outro caminho deixar o veredito baixo com a
                lista vazia, o texto continua honesto. */}
            {verificacaoIA.confianca === "alta"
              ? "✅ Verificação de fidelidade: ALTA"
              : verificacaoIA.confianca === "media"
              ? "⚠️ Verificação de fidelidade: MÉDIA — revisar observações"
              : (verificacaoIA.problemas || []).length > 0
              ? "❌ Verificação de fidelidade: BAIXA — problemas identificados"
              : "⚠️ Verificação de fidelidade: BAIXA — sem problema específico apontado; revise o laudo"}
          </div>
          {verificacaoIA.problemas && verificacaoIA.problemas.length > 0 && (
            <ul
              style={{
                margin: "4px 0 0 0",
                paddingLeft: "20px",
                color: "#555",
              }}
            >
              {/* B370 — item pode vir como OBJETO (hard-block de placeholder);
                  desenhar objeto é React #31 e apagava a tela inteira. */}
              {textosDosProblemas(verificacaoIA.problemas).map((p, i) => (
                <li key={i} style={{ marginBottom: "2px" }}>
                  {p}
                </li>
              ))}
            </ul>
          )}
          {verificacaoIA.sugestoes && verificacaoIA.sugestoes.length > 0 && (
            <div style={{ marginTop: "6px", color: "#666", fontSize: "12px" }}>
              <strong>Sugestões:</strong> {verificacaoIA.sugestoes.join(" | ")}
            </div>
          )}
          {/* 🕶️ B359 Fase 2 Passo 1 (AI_158): registro do verificador em modo
              SOMBRA (candidato rodando em paralelo, só para comparação). Não
              muda o selo acima; some quando a sombra for desligada. */}
          {registrosSombra(provenienciaIA).map((reg, i) => {
            const txt = textoSombraVerificador(reg);
            if (!txt) return null;
            const achados = linhasAchadosSombra(reg);
            return (
              <div key={i} style={{ marginTop: "6px", color: "#6b7280", fontSize: "12px" }}>
                🕶️ {txt}
                {achados.length > 0 && (
                  <details style={{ marginTop: "2px" }}>
                    <summary style={{ cursor: "pointer" }}>
                      o que a sombra apontou ({achados.length})
                    </summary>
                    <ul style={{ margin: "2px 0 0 0", paddingLeft: "18px" }}>
                      {achados.map((l, j) => (
                        <li key={j} style={{ marginBottom: "2px" }}>{l}</li>
                      ))}
                    </ul>
                  </details>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* BOTÃO GERAR PDF */}
      <button
        onClick={handleGerarPDF}
        disabled={!titulo.trim() || !conteudo.trim()}
        style={{
          padding: "10px 24px",
          background:
            !titulo.trim() || !conteudo.trim() ? theme.gray[300] : "#16a34a",
          color: "white",
          border: "none",
          borderRadius: theme.borderRadius.md,
          fontSize: "14px",
          fontWeight: "700",
          cursor:
            !titulo.trim() || !conteudo.trim() ? "not-allowed" : "pointer",
          fontFamily: theme.fonts.sans,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "8px",
          width: "100%",
        }}
      >
        <FileDown size={18} /> Gerar Documento (PDF)
      </button>

      {/* B289 (AI_127): ASSINAR DIGITALMENTE (médico-only + flag de habilitação) */}
      {ehMedico(usuarioLogado) && ASSINATURA_HABILITADA && (
        <button
          onClick={handleAssinarTextoLivre}
          disabled={!titulo.trim() || !conteudo.trim()}
          style={{
            padding: "10px 24px",
            marginTop: "8px",
            background:
              !titulo.trim() || !conteudo.trim()
                ? theme.gray[300]
                : theme.primary[600],
            color: "white",
            border: "none",
            borderRadius: theme.borderRadius.md,
            fontSize: "14px",
            fontWeight: "700",
            cursor:
              !titulo.trim() || !conteudo.trim() ? "not-allowed" : "pointer",
            fontFamily: theme.fonts.sans,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "8px",
            width: "100%",
          }}
        >
          <ShieldCheck size={18} /> Assinar digitalmente
        </button>
      )}

      {modalAssinatura && (
        <ModalAssinatura
          aberto={true}
          onFechar={() => setModalAssinatura(null)}
          html={modalAssinatura.html}
          tipo={modalAssinatura.tipo}
          prescricaoId={modalAssinatura.prescricaoId}
          dados={modalAssinatura.dados}
          nomeArquivo={modalAssinatura.nomeArquivo}
          codigoValidacao={modalAssinatura.codigoValidacao}
          dadosValidacao={modalAssinatura.dadosValidacao}
        />
      )}
    </div>
  );
}
